import { DataType } from "@/types/enums/DataType";
import { noValueOperators } from "./condition-utils";

// Single source of truth for condition operators: token (the stored backend
// word), glyph (compact symbol for collapsed chips), section (operator-picker
// grouping) and value shape. Display words live in i18n as
// `rulesAndLimits.operator_<token>` — one namespace for every surface.
// PFilter's legacy glyph vocabulary is migrated onto this registry in the
// guest-list ConditionGroup migration (see condition-converters).

export { noValueOperators };

export type OperatorSection = "standard" | "advanced";

export interface OperatorMeta {
  token: string;
  /** Compact symbol for collapsed chips. Absent → always show the word. */
  glyph?: string;
  /** Takes no value input (is_empty family). */
  valueless: boolean;
  /** Value is an array of picks (in / not_in). */
  multiValue: boolean;
  section: OperatorSection;
  /** Hover example in the picker; `field` is the already-picked field label. */
  example?: (field: string) => string;
}

export const OPERATORS: Record<string, OperatorMeta> = {
  "=": { token: "=", glyph: "=", valueless: false, multiValue: false, section: "standard", example: (f) => `${f} = 30` },
  "!=": { token: "!=", glyph: "≠", valueless: false, multiValue: false, section: "standard", example: (f) => `${f} ≠ 30` },
  ">": { token: ">", glyph: ">", valueless: false, multiValue: false, section: "standard", example: (f) => `${f} > 30` },
  "<": { token: "<", glyph: "<", valueless: false, multiValue: false, section: "standard", example: (f) => `${f} < 30` },
  ">=": { token: ">=", glyph: "≥", valueless: false, multiValue: false, section: "standard", example: (f) => `${f} ≥ 30` },
  "<=": { token: "<=", glyph: "≤", valueless: false, multiValue: false, section: "standard", example: (f) => `${f} ≤ 30` },
  contains: { token: "contains", glyph: "~", valueless: false, multiValue: false, section: "advanced", example: (f) => `${f} ~ "VIP"` },
  not_contains: { token: "not_contains", glyph: "!~", valueless: false, multiValue: false, section: "advanced", example: (f) => `${f} !~ "VIP"` },
  is_empty: { token: "is_empty", glyph: "∅", valueless: true, multiValue: false, section: "advanced", example: (f) => `${f} = ∅` },
  is_not_empty: { token: "is_not_empty", glyph: "⊙", valueless: true, multiValue: false, section: "advanced", example: (f) => `${f} ≠ ∅` },
  in: { token: "in", glyph: "∈", valueless: false, multiValue: true, section: "advanced", example: (f) => `${f} ∈ {A, B}` },
  not_in: { token: "not_in", glyph: "∉", valueless: false, multiValue: true, section: "advanced", example: (f) => `${f} ∉ {A, B}` },
  changed_to: { token: "changed_to", glyph: "→", valueless: false, multiValue: false, section: "advanced", example: (f) => `${f} → 30` },
  changed_from: { token: "changed_from", glyph: "←", valueless: false, multiValue: false, section: "advanced", example: (f) => `${f} ← 30` },
  matches_new: { token: "matches_new", glyph: "⧉", valueless: true, multiValue: false, section: "advanced" },
};

// Base operators per field data type. `matches_new` is intentionally NOT here —
// it is a website-builder dedup operator (cross-references the registering
// guest) that the check-in condition evaluators do not implement
// (`default: return false`), so offering it on a device/check-in condition
// would silently block every check-in. It is appended only when
// `enableMatchesNew` is set (WebsiteStatus).
const baseOperatorsForType: Record<string, string[]> = {
  [DataType.STRING]: ["=", "!=", "contains", "not_contains", "is_empty", "is_not_empty"],
  [DataType.LONG]: ["=", "!=", ">", "<", ">=", "<=", "is_empty", "is_not_empty"],
  [DataType.DOUBLE]: ["=", "!=", ">", "<", ">=", "<=", "is_empty", "is_not_empty"],
  [DataType.CURRENCY]: ["=", "!=", ">", "<", ">=", "<=", "is_empty", "is_not_empty"],
  [DataType.BOOLEAN]: ["=", "!="],
  [DataType.DATE]: ["=", "!=", ">", "<", ">=", "<=", "is_empty", "is_not_empty"],
  [DataType.DATE_TIME]: ["=", "!=", ">", "<", ">=", "<=", "is_empty", "is_not_empty"],
  [DataType.SINGLE_SELECT]: ["=", "!=", "contains", "not_contains", "in", "not_in", "is_empty", "is_not_empty"],
  [DataType.MULTI_SELECT]: ["contains", "not_contains", "in", "not_in", "is_empty", "is_not_empty"],
  [DataType.EMAIL]: ["=", "!=", "contains", "not_contains", "is_empty", "is_not_empty"],
  [DataType.URL]: ["=", "!=", "contains", "not_contains", "is_empty", "is_not_empty"],
  // IMAGE stores a backend-minted object — only presence checks make sense.
  [DataType.IMAGE]: ["is_empty", "is_not_empty"],
  // is_empty/is_not_empty: checkedIn is tri-state (true = in, false =
  // checked OUT, null = never scanned) — without ∅ the "never scanned"
  // state is inexpressible and "= false" is the only (wrong) proxy.
  [DataType.CHECK_IN_STATUS]: ["=", "!=", "is_empty", "is_not_empty"],
  [DataType.EMAIL_STATUS]: ["=", "!=", "is_empty", "is_not_empty"],
  [DataType.CHECK_IN_LOG_ACTION]: ["=", "!="],
  default: ["=", "!="],
};

const transitionOperators = ["changed_to", "changed_from"];

const transitionSupportedTypes = new Set<string>([
  DataType.STRING,
  DataType.LONG,
  DataType.DOUBLE,
  DataType.CURRENCY,
  DataType.BOOLEAN,
  DataType.DATE,
  DataType.DATE_TIME,
  DataType.SINGLE_SELECT,
  DataType.GUEST_STATUS,
  DataType.CHECK_IN_STATUS,
  DataType.EMAIL,
  DataType.URL,
]);

// Guest-identity types that may offer `matches_new` when the host enables it.
export const matchesNewSupportedTypes = new Set<string>([
  DataType.STRING,
  DataType.LONG,
  DataType.DOUBLE,
  DataType.CURRENCY,
  DataType.BOOLEAN,
  DataType.DATE,
  DataType.DATE_TIME,
  DataType.SINGLE_SELECT,
  DataType.MULTI_SELECT,
  DataType.EMAIL,
  DataType.URL,
]);

export interface OperatorOptions {
  enableTransitionOperators?: boolean;
  enableMatchesNew?: boolean;
  /** Future opt-in operators (e.g. `between`) — appended when the backend of
   *  the hosting surface supports them; threading mirrors enableMatchesNew. */
  enabledOperators?: string[];
}

export function operatorsForType(
  dataType: string,
  opts: OperatorOptions = {},
): string[] {
  let ops = baseOperatorsForType[dataType] || baseOperatorsForType.default;
  if (opts.enableTransitionOperators && transitionSupportedTypes.has(dataType)) {
    ops = [...ops, ...transitionOperators];
  }
  if (opts.enableMatchesNew && matchesNewSupportedTypes.has(dataType)) {
    ops = [...ops, "matches_new"];
  }
  if (opts.enabledOperators?.length) {
    ops = [...ops, ...opts.enabledOperators.filter((o) => !ops.includes(o))];
  }
  return ops;
}

export function operatorGlyph(token: string): string | undefined {
  return OPERATORS[token]?.glyph;
}

/** Translated word for an operator token ("ist gleich", "enthält", …).
 *  `t` must be the `rulesAndLimits` translator. */
export function operatorWord(
  t: (key: string) => string,
  token: string,
): string {
  return t(`operator_${token}`);
}

export function operatorExample(
  token: string,
  fieldLabel: string,
): string | undefined {
  return OPERATORS[token]?.example?.(fieldLabel);
}

/**
 * Hybrid display form for the operator control and picker: universally-read
 * math symbols (= ≠ > < ≥ ≤) stay symbols — faster to scan for non-technical
 * users; everything whose glyph is cryptic (~ ∅ ⊙ ∈ ∉ → ← ⧉) shows its
 * translated word instead. The picker's hover hint always carries the other
 * form, so both stay learnable.
 */
export function operatorDisplay(
  t: (key: string) => string,
  token: string,
): string {
  const meta = OPERATORS[token];
  if (meta?.section === "standard" && meta.glyph) return meta.glyph;
  return operatorWord(t, token);
}

export function isStandardOperator(token: string): boolean {
  return OPERATORS[token]?.section === "standard";
}
